/* eslint-disable class-methods-use-this */
/* eslint-disable  prefer-promise-reject-errors */
/**
 * @name Util
 * @description Convenience method to load all utility functions
 * @class
 * @example
 * import Util from './util';
 *
 * // INITIALIZE
 * this.util = new Util('component-name');
 *
 * // USE
 * this.router.setParam('category', 1); // SET URL PARAM
 */

// THIRD-PARTY
import { bindActionCreators } from "redux";
import { get, isEmpty, isPlainObject, merge } from "lodash";
import qs from "query-string";
import { RedirectionError } from "@lululemon/mwa-utils";
import { withScope } from "@sentry/browser";
// CUSTOM
import { poll } from "@lululemon/mwa-ab-testing/esm/utils";
import Performance from "./performance";
import { getLocaleForContext } from "@lululemon/ecom-common/src/util/locale-utils";

export class UnhandledApiError extends Error {
  constructor(message, requestOptions, extraProps) {
    super(message);
    this.name = "UnhandledApiError";
    this.requestOptions = requestOptions;
    Object.assign(this, extraProps);
  }
}

async function resolveResponseBody(response) {
  if (response) {
    const contentType = response.headers.get("content-type");
    const isJSON = contentType?.match(/application\/(json|vnd\.api\+json)/);

    return isJSON ? response.json() : response.text();
  }
}

export default class Util {
  constructor(namespace) {
    if (typeof namespace === "undefined") namespace = "lulu";
    this.namespace = namespace;
    this.performance = new Performance();
    mixinGlobalLogger(this, this.windowEvent());
  }

  handleError(opts, response, responseBody, error, sentryLogLevel = "error") {
    const onErrorPage = globalThis.window?.location.href.includes("error/");
    const fault = response
      ? `call.error.resp-${JSON.stringify(response.status)}`
      : "call.error-network";
    let errorHandled = false;

    withScope((scope) => {
      // Set tags in child scope to ensure important data is captured by logging calls in any custom
      // error handler
      scope.setTags({
        req_url: opts.url,
        fault,
        isRedirect: opts.redirect,
        correlationId: opts.headers["x-lll-ecom-correlation-id"] || "",
      });
      scope.setFingerprint([
        response?.status || "network-error",
        opts.url,
        this.namespace,
      ]);
      scope.setLevel(sentryLogLevel);

      if (response) {
        errorHandled = Boolean(opts.onRequestError?.(response, scope, opts));

        if (!errorHandled) {
          console.error(JSON.stringify(response));
        }
      } else {
        errorHandled = Boolean(opts.onNetworkError?.(error, scope, opts));
      }
    });

    if (!errorHandled) {
      if (opts.redirect) {
        if (onErrorPage) return;

        const redirectionErr = response
          ? new RedirectionError({
              message: `Encountered a request error while making an API request`,
              url: `/error/${response.status}?component=${encodeURIComponent(
                this.namespace
              )}`,
            })
          : new RedirectionError({
              message: `Encountered a network error while making an API request`,
              url: `/error/network?component=${encodeURIComponent(
                this.namespace
              )}`,
            });
        redirectionErr.cause = error;
        throw redirectionErr;
      }

      throw new UnhandledApiError("Encountered an unhandled API error", opts, {
        data: responseBody,
        raw: response,
        body: responseBody?.error || responseBody,
        status: responseBody?.errorCode,
      });
    }
  }

  async api(
    options,
    sentryLogLevel = "error",
    /* DEPRECATED */
    sentryScopeName = this.SENTRY_TRX_NAME
  ) {
    const opts = merge(
      {
        redirect: false,
        method: "get",
        mode: "cors",
        headers: {},
        credentials: "include",
      },
      options
    );
    const fetch = opts.fetch || globalThis.fetch;

    try {
      const response = await fetch(opts.url, {
        method: opts.method,
        headers: opts.headers,
        mode: opts.mode,
        credentials: opts.credentials,
        body: opts.body || undefined,
      });

      if (!response.ok) {
        // Stupid hack since for some reason making `handleError()` async in any way causes
        // unhandled promise rejections no matter what I do
        const responseBody = await resolveResponseBody(response?.clone());
        return this.handleError(
          opts,
          response,
          responseBody,
          null,
          sentryLogLevel
        );
      }

      return {
        data: await response.json(),
        raw: { headers: response.headers },
      };
    } catch (error) {
      if (
        error instanceof RedirectionError ||
        error instanceof UnhandledApiError
      ) {
        throw error;
      }

      return this.handleError(opts, null, null, error, sentryLogLevel);
    }
  }

  handleRedirects(redirectObj) {
    window.location.href = redirectObj.url;
  }

  // PULL QUERY PARAMS FROM URL
  getParams(url, query) {
    const opts = {};
    let params = url.split("?");

    params = params.length > 1 ? params[1].split("&") : params;

    params.map((term) => {
      term = term.split("=");
      term[0] = term[0].toLowerCase();
      opts[term[0]] = term[1];
    });

    if (query && opts[query]) {
      return opts[query];
    }

    return opts;
  }

  // DETERMINE IF DEVICE HAS TOUCH CAPABILITY
  isTouch() {
    let touch = false;
    if (typeof document !== "undefined")
      touch = "ontouchstart" in document.documentElement;
    return touch;
  }

  windowEvent() {
    return global || window;
  }

  // RETURN DATA FROM REDUX RTOE
  mapDispatchToProps(dispatch) {
    return bindActionCreators(
      {
        dispatch,
      },
      dispatch
    );
  }

  // RETURN DATA FROM REDUX STORE
  getPageData(page, store) {
    return new Promise((resolve, reject) => {
      store.subscribe(() => {
        const data = store.getState().rootReducer.page[page];
        if (data) {
          resolve(data);
        } else {
          reject(false);
        }
      });
    });
  }

  getRobotTextForSeo(noIndexVal, noFollowValue) {
    const robotTextArr = [];
    try {
      Number(noIndexVal) === 0
        ? robotTextArr.push("index")
        : robotTextArr.push("noindex");
      Number(noFollowValue) === 0
        ? robotTextArr.push("follow")
        : robotTextArr.push("nofollow");
      robotTextArr.push.apply(robotTextArr, ["noodp", "noydir"]);
      const splitContent = robotTextArr.join(", ");
      return { name: "robots", content: splitContent };
    } catch (ex) {
      return { name: "robots", content: "index, follow, noodp, noydir" };
    }
  }

  // PARSE HEAD TAGS FROM API STRING
  getHrefLangTagsForSeo(headData) {
    // NFKD changes the original data to remove whitespace issues NFKC could of also been used
    const normalizedHeadData = headData && headData.normalize("NFKD");
    const baseRes = {
      linkTags: [],
      metaTags: [],
    };

    if (!headData) return baseRes;

    // Split up the base tags
    const split = normalizedHeadData
      .replace(/'/g, '"')
      .split("\n")
      .join("")
      .split("/>");
    if (!split) {
      return baseRes;
    }

    // Reduce over the tags to build results
    return split.reduce((results, tag) => {
      if (!tag) {
        return results;
      }

      // Figure out the type of the tag and split out the body
      let type;
      let tagBody;
      if (tag.indexOf("<link") > -1) {
        type = "link";
        tagBody = tag.split("<link ").join("");
      } else if (tag.indexOf("<meta") > -1) {
        type = "meta";
        tagBody = tag.split("<meta ").join("");
      }

      // Throw out tags witout any content
      if (!type || !tagBody) {
        return results;
      }

      // Get the attribute values for the tag
      const attributes = tagBody.split('" ').reduce((attrs, attr) => {
        const values = attr.split("=");
        if (typeof values[1] !== "undefined") {
          return {
            ...attrs,
            [values[0]]: values[1].split('"').join(""),
          };
        }

        return attrs;
      }, {});

      // Build the new tag obj
      const newTag = {
        ...attributes,
        type,
      };

      // Append to results if it's a supported tag type
      switch (type) {
        case "link":
          return {
            ...results,
            linkTags: [...results.linkTags, newTag],
          };
        case "meta":
          return {
            ...results,
            metaTags: [...results.metaTags, newTag],
          };
        default:
          return results;
      }
    }, baseRes);
  }

  cleanupSearchTerm(term = "") {
    return sanitize(term);
  }

  /**
   * @description Test if all required data has a value. If so, returns a 'pass'.
   * If not, returns a 'fail' and an array of missing fields.
   * @param {Array|Object} items - Required fields that are expected to have a value.
   * @returns {Object} Pass/fail grade and an array of missing fields.
   */
  requiredData(items) {
    let pass = true;
    let missing = [];

    // ** Deprecated (maintaining backwards-compatibility)
    if (Array.isArray(items)) {
      items.map((item) => {
        if (typeof item.value === "undefined" || !item.value) {
          pass = false;
          missing.push(item.name);
        }
      });
    } else {
      /**
       * Value that coerces to false (excluding 0), is undefined,
       * or is {} is considered to be missing.
       */
      missing = Object.keys(items).filter(
        (key) =>
          (!items[key] && items[key] !== 0) ||
          (isEmpty(items[key]) &&
            (isPlainObject(items[key]) || Array.isArray(items[key])))
      );
      pass = !missing.length;
    }
    return {
      pass,
      missing,
    };
  }

  /**
   * @description Capitilize each word in a given string
   * @param {String}
   * @returns {String}
   */
  static stringToTitleCase(string) {
    if (typeof string !== "string") return "";
    return string.replace(/(^|[^a-zA-Z'])([a-z])/g, (match, p1, p2) => {
      const param2 = p2.toUpperCase();
      return p1 + param2;
    });
  }

  /**
   * @description Detect if a URL has a success response without loading the body content.
   * Must be same-origin and will not work server-side.
   * @param {string} validateUrl
   * @returns {boolean}
   */
  static isValidURL(validateUrl, callback) {
    if (typeof window !== "undefined" && window.XMLHttpRequest) {
      const request = new XMLHttpRequest();
      request.onreadystatechange = () => {
        if (request.readyState !== 4) return;
        if (
          request.status === 200 ||
          request.status === 206 ||
          request.status === 302
        ) {
          callback(true);
        } else {
          callback(false);
        }
      };
      // confirm URL is valid without loading the content
      request.open("HEAD", validateUrl);
      request.send();
    } else {
      callback(false);
    }
  }

  /**
   * Utility function to read value of 'UsrLocale' cookie
   * @function
   * @returns {String}
   * @deprecated Do not use anymore, use useRouter() or hook
   */
  static getShippingLocation() {
    // option to override by URL parameter, for use on localhost
    const { sl } = qs.parse(get(global, "location.search"));
    const slSanitized = ["US", "CA", "FR"].includes(sl?.toUpperCase())
      ? sl?.toUpperCase()
      : false;
    const locale = getLocaleForContext()?.slice(-2); // default US

    return slSanitized || locale;
  }
}

/**
 * @description extend/override the target's logger with the source's logger.
 * used to inject the ssr logger
 * @param target {Object}
 * @param source {Object}
 */
export function mixinGlobalLogger(target, source) {
  const { logger } = source;
  const globalTarget = target;
  if (logger) {
    globalTarget.logger.log = logger.info;
    globalTarget.logger.info = logger.info;
    globalTarget.logger.debug = logger.debug;
    globalTarget.logger.warn = logger.warn;
    globalTarget.logger.error = logger.error;
  }
}

/* eslint-disable no-useless-escape */
export function sanitize(text = "") {
  /**
   * Note on the disallowedCharacters regex: The caret inside of a character class
   * [^ ] is the negation operator
   */
  // ^ - Matches anything NOT in the character class
  // In this case we are saying exclude all but these characters
  const disallowedCharacters = /[^\w\_\-\/\s\+\#\&éüöêåø]/gi;
  /**
   * Allow periods and leading spaces(if any) for search term "T.H.E. short/sock"
   * Regex grouping will cover sanitizing post string and remove symbols except "T.H."
   */
  const strMatcher = /(^\s*[t]\.[h]\.)(.*)/i;
  const FIRST_GROUP = 1;
  const SECOND_GROUP = 2;
  // word starts or ends with dash and two or more consecutive dashes
  const lonelyDashes = /^\-+|\s\-+|\-+\s|\-\-+|\-+$/g;
  const lonelySlashes = /^\/+|\s\/+|\/+\s|\/\/+|\/+$/g;

  /**
   * Todo: Move this logic to BE while integrating with Lucidworks
   * Leave the dots after initial T.H. and strip out any symbols afterwards
   */
  if (strMatcher.exec(text)) {
    const patternGroups = strMatcher.exec(text.trim());
    const sanitizedText = patternGroups[SECOND_GROUP].replace(
      disallowedCharacters,
      ""
    );
    return `${patternGroups[FIRST_GROUP]}${sanitizedText}`;
  }
  return [disallowedCharacters, lonelyDashes, lonelySlashes]
    .reduce((textVal, sanitizer) => textVal.replace(sanitizer, ""), text)
    .trim();
}
export function stripNtt(text = "") {
  const ntt = /^\?Ntt\=/;
  return text.replace(ntt, "");
}

/* eslint-enable */
export const loadScript = (src, callback, id) => {
  if (!src) {
    return;
  }
  let js;
  const d = document;
  const ijs = d.getElementsByTagName("script")[0];
  if (id && d.getElementById(id)) {
    // run callback as script is already loaded
    if (callback) {
      callback();
    }
    return;
  }
  js = d.createElement("script");
  js.id = id;
  js.onload = () => {
    if (callback) {
      callback();
    }
  };
  js.src = src;

  if (ijs && ijs.parentNode) {
    ijs.parentNode.insertBefore(js, ijs);
  }
};

export const loadScriptWhenReady = (src, callback, id) => {
  if (process.env.NODE_ENV === "test") {
    if (callback) {
      callback();
    }
    return;
  }
  if (document.readyState === "complete") {
    // if page is already loaded, get the script
    loadScript(src, callback, id);
  } else {
    // if page is not yet loaded, add an event listener
    window.addEventListener("load", () => {
      loadScript(src, callback, id);
    });
  }
};

export const captureDevTelemetry = ({ ticket, name }) => {
  if (!ticket || !name) {
    console.error(
      "captureDevTelemetry: missing config. Please make sure ticket and name are passed"
    );
    return null;
  }

  const aLaunch_waeEID = (window.aLaunch_waeEID = window.aLaunch_waeEID || {});
  aLaunch_waeEID.version = 1.0;
  window.aLaunch_waeEID.experience = window.aLaunch_waeEID.experience || [];
  const reportObject = {
    ticket,
    name,
    variation: "v1",
  };
  aLaunch_waeEID.experience.push(reportObject);
  const temp = {};
  aLaunch_waeEID.experience.forEach((item) => {
    temp[`${item.ticket}-${item.name}`] = item;
  });
  aLaunch_waeEID.experience = Object.values(temp);
};

export const deferStartTillUserAction = (cb) => {
  if (process.env.NODE_ENV === "test") {
    if (cb) {
      cb();
    }
    return;
  }
  if (!process.browser) {
    return;
  }

  let locked = true;
  let didClick = false;
  let didScroll = false;

  function unlock() {
    if (locked) {
      locked = false;
      cb();
    }
  }

  function deferMove() {
    unlock();
  }

  function deferTouch(event) {
    if (locked) {
      deferMove(event);
      const { target } = event; // on iOS links can be not clicked through images

      // setTimeout(function () {
      if (!didClick && !didScroll) {
        if (target && target.click) {
          target.click();
        }
      }
      // }, 1000);
    }
  }

  function deferScroll(event) {
    didScroll = true;
    deferMove(event);
  }

  function deferClick(event) {
    didClick = true;

    if (locked) {
      unlock();
      // var target = event.target; // repeat click event in a second

      // event.preventDefault();
      // setTimeout(function () {
      //   target.click();
      // }, 1000);
    }
  }

  document.addEventListener("click", deferClick, true);
  document.addEventListener("mousemove", deferMove, true);
  document.addEventListener("keydown", deferMove, true);
  document.addEventListener("touchstart", deferTouch, true);
  window.addEventListener("scroll", deferScroll, {
    capture: true,
    passive: true,
  }); // TODO: start js if some placeholder (pagination?, lazy image?) is visible

  // lighthouse magically known about timers
  // setTimeout(unlock, 10000);
};

const getRemoteComponent = ({ remote, component }) =>
  poll(
    (resolve) => {
      const remoteComponent = global?.[remote]?.get?.(component);
      if (remoteComponent?.then) {
        resolve({ remote: global[remote], component: remoteComponent });
      }
    },
    20,
    2000
  );

export const runFederation = () => {
  if (!process.browser) return;
  captureDevTelemetry({ ticket: "WAE-1159", name: "bingLoaded" });
  getRemoteComponent({ remote: "abtests", component: "analytics/bing" })
    .then(({ component }) => component)
    .then((factory) => {
      factory();
    })
    .catch((err) => {
      console.error("window.abtests(analytics/bing) does not exist", err);
    });
  deferStartTillUserAction(() => {
    captureDevTelemetry({
      ticket: "WAE-1159",
      name: "bingDeferStartTillUserAction",
    });
  });
  window.requestIdleCallback(
    () => {
      captureDevTelemetry({ ticket: "WAE-1159", name: "bingIdleCallback" });
    },
    { timeout: 2000 }
  );
};
export const runVendorFederation = () => {
  if (!process.browser) return;
  if (document.readyState !== "complete") {
    document.addEventListener("readystatechange", () => {
      if (document.readyState === "complete") {
        runFederation();
      }
    });
  } else {
    runFederation();
  }
};

/**
 * Verify whether to allow draft Contentful API entries to be returned.
 * Allow draft entries if URL contains query string `?preview=1` and the
 * previewToken is available.
 * @returns {boolean}
 */
export const isContentfulPreview = (queryStringOrObj, previewToken) => {
  const isQueryString = typeof queryStringOrObj === "string";
  const queryObj = isQueryString
    ? qs.parse(queryStringOrObj)
    : queryStringOrObj;
  const hasPreviewParam = queryObj?.preview === "1";
  const hasPreviewToken = previewToken != null;

  return hasPreviewParam && hasPreviewToken;
};

/**
 * Takes an html string and parses out text (stripping the tags and html entities)
 * Uses html-to-text lib for SSR and a lighter client-side approach
 *
 * @param {string} str
 * @returns {string}
 */
export const stripHtml = (str) => {
  if (typeof window === "undefined") {
    const { htmlToText } = require("html-to-text");

    return htmlToText(str);
  }

  const div = document.createElement("div");
  div.innerHTML = str;

  return div.textContent ?? div.innerText ?? "";
};

Util.sanitize = sanitize;
Util.stripNtt = stripNtt;
